import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../../components/Toast';
import { Logo } from '../../components/Logo';
import { KiroMascot } from '../../components/KiroMascot';

/**
 * Wizard de onboarding pós-signup — modal fullscreen.
 *
 * Aparece **uma única vez** no primeiro login do owner. Diferente do
 * `TenantOnboardingBanner` (banner persistente com checklist clicável),
 * o wizard é guiado: 6 passos lineares com gradient, ilustração SVG,
 * progresso visual e back/next/skip.
 *
 * Detecção:
 *   - Endpoint `GET /tenants/me/onboarding` retorna `complete:false`
 *   - `user.role === 'owner'` (não-owners caem só no banner)
 *   - `localStorage[STORAGE_KEY] !== '1'` (uma vez dispensado, fica)
 *   - `navigator.webdriver` é false (E2E pula)
 *
 * Steps:
 *   1. Boas-vindas — confirma nome da clínica (PATCH /tenants/me/name)
 *   2. WhatsApp principal — opcional, grava em themeJson via /branding
 *   3. Horário de atendimento — sliders simplificados (PATCH /me/schedule)
 *   4. Primeiro serviço — cria de verdade (POST /services; code auto-gerado)
 *   5. Logo — placeholder (upload real fica pra depois; só link p/ Settings)
 *   6. Primeiro paciente — só CTA (criação real tem CPF/endereço required)
 *
 * Cada step salva no submit "Próximo". Falhas mostram toast mas não
 * bloqueiam — o usuário pode pular pra continuar e voltar depois.
 */

const STORAGE_KEY = 'dentalflow.wizardShown';

interface OnboardingStatus {
  complete: boolean;
}

interface TenantMe {
  id: string;
  name: string;
  scheduleStartHour: number;
  scheduleEndHour: number;
}

interface TenantBranding {
  name: string;
  themeJson: Record<string, unknown> | null;
}

export function OnboardingWizard() {
  const { user } = useAuth();
  const isE2E =
    typeof navigator !== 'undefined' && navigator.webdriver === true;
  const isOwner = user?.role === 'owner';

  // Flag de "wizard já mostrado" é POR TENANT — senão, ao testar/usar
  // várias clínicas no mesmo navegador, dispensar numa pula o wizard de
  // TODAS as outras (inclusive a clínica nova que acabou de ser criada).
  const tenantKey = user?.tenantId
    ? `${STORAGE_KEY}.${user.tenantId}`
    : STORAGE_KEY;

  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(tenantKey) === '1';
  });

  // Re-sincroniza quando o tenant muda (troca de clínica / login tardio).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setDismissed(window.localStorage.getItem(tenantKey) === '1');
  }, [tenantKey]);

  const enabled = !!user && isOwner && !isE2E && !dismissed;

  const { data: status, isLoading } = useQuery<OnboardingStatus>({
    queryKey: ['tenant-onboarding'],
    queryFn: async () => (await api.get('/tenants/me/onboarding')).data,
    enabled,
    staleTime: 60_000,
    retry: 1,
  });

  // Só mostra se: owner + não dispensou + tenant ainda incompleto.
  const open = enabled && !isLoading && !!status && !status.complete;

  const handleClose = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(tenantKey, '1');
    }
    setDismissed(true);
  };

  if (!open) return null;
  return <WizardModal onClose={handleClose} />;
}

// ───────────────────────────────────────────────────────────────────
// Modal — encapsula os steps. Separado pra evitar montar formulários
// pesados antes de saber que o wizard vai aparecer.
// ───────────────────────────────────────────────────────────────────
function WizardModal({ onClose }: { onClose: () => void }) {
  const [stepIdx, setStepIdx] = useState(0);
  const nav = useNavigate();

  const steps = useMemo(
    () => [
      { id: 'welcome', label: 'Boas-vindas' },
      { id: 'contact', label: 'Contato' },
      { id: 'schedule', label: 'Horário' },
      { id: 'service', label: 'Serviços' },
      { id: 'logo', label: 'Logo' },
      { id: 'patient', label: 'Pacientes' },
    ],
    [],
  );

  const goNext = () => {
    if (stepIdx < steps.length - 1) setStepIdx((i) => i + 1);
    else onClose();
  };
  const goBack = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const finish = () => {
    onClose();
  };

  const goPatients = () => {
    onClose();
    nav('/pacientes');
  };

  const currentStep = steps[stepIdx]!.id;

  return (
    <div
      className="fixed inset-0 z-50 bg-white overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wizard-title"
      data-testid="onboarding-wizard"
    >
      {/* Header com progresso + skip-all */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-brand-600 via-brand-500 to-accent-500 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <Logo variant="lockup" size={28} inverted />
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white text-sm font-medium"
            data-testid="wizard-skip-all"
          >
            Pular agora
          </button>
        </div>
        <ProgressBar current={stepIdx} total={steps.length} />
      </div>

      {/* Conteúdo */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {currentStep === 'welcome' && (
          <WelcomeStep onNext={goNext} onSkip={goNext} />
        )}
        {currentStep === 'contact' && (
          <ContactStep onNext={goNext} onBack={goBack} onSkip={goNext} />
        )}
        {currentStep === 'schedule' && (
          <ScheduleStep onNext={goNext} onBack={goBack} onSkip={goNext} />
        )}
        {currentStep === 'service' && (
          <ServiceStep onNext={goNext} onBack={goBack} onSkip={goNext} />
        )}
        {currentStep === 'logo' && (
          <LogoStep onNext={goNext} onBack={goBack} onSkip={goNext} />
        )}
        {currentStep === 'patient' && (
          <PatientStep
            onFinish={finish}
            onBack={goBack}
            onGoPatients={goPatients}
          />
        )}
      </div>
    </div>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = Math.round(((current + 1) / total) * 100);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-3">
      <div className="flex items-center justify-between text-xs text-white/85 mb-1.5">
        <span>
          Passo {current + 1} de {total}
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 w-full bg-white/20 rounded-full overflow-hidden">
        <div
          className="h-full bg-white rounded-full transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 1 — Boas-vindas + confirmar nome da clínica.
// ───────────────────────────────────────────────────────────────────
function WelcomeStep({
  onNext,
  onSkip,
}: {
  onNext: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: tenant } = useQuery<TenantMe>({
    queryKey: ['tenant-me'],
    queryFn: async () => (await api.get('/tenants/me')).data,
  });

  const [name, setName] = useState('');

  useEffect(() => {
    if (tenant?.name) setName(tenant.name);
  }, [tenant]);

  const saveMut = useMutation({
    mutationFn: () => api.patch('/tenants/me/name', { name: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-me'] });
      qc.invalidateQueries({ queryKey: ['tenant-onboarding'] });
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Falha ao salvar nome.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 3) {
      toast.error('Informe um nome com pelo menos 3 caracteres.');
      return;
    }
    saveMut.mutate();
  };

  return (
    <StepLayout
      illustration={<KiroMascot name="feliz" size={132} fallback={<WelcomeArt />} />}
      title="Olá! Eu sou o Kiro 👋"
      subtitle="Seu assistente no Kevins Clínicas. Vamos configurar sua clínica em poucos passos rápidos — leva menos de 3 minutos."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-ink-700">
            Nome da clínica
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Clínica Sorria"
            className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            data-testid="wizard-clinic-name"
            autoFocus
          />
          <span className="mt-1 block text-xs text-ink-500">
            Aparece em e-mails, faturas e na tela de espera. Você pode mudar
            depois.
          </span>
        </label>
        <StepActions
          showBack={false}
          onSkip={onSkip}
          onNext={() => handleSubmit({ preventDefault: () => {} } as any)}
          nextLabel={saveMut.isPending ? 'Salvando…' : 'Próximo'}
          nextDisabled={saveMut.isPending}
        />
      </form>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 2 — Telefone / WhatsApp principal.
// ───────────────────────────────────────────────────────────────────
function ContactStep({
  onNext,
  onBack,
  onSkip,
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: branding } = useQuery<TenantBranding>({
    queryKey: ['tenant-branding'],
    queryFn: async () => (await api.get('/tenants/me/branding')).data,
  });

  const [phone, setPhone] = useState('');

  useEffect(() => {
    const stored = (branding?.themeJson?.['phone'] as string | undefined) ?? '';
    if (stored) setPhone(stored);
  }, [branding]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch('/tenants/me/branding', {
        theme: { phone: phone.trim() || null },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-branding'] });
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Falha ao salvar telefone.');
    },
  });

  const handleNext = () => {
    if (!phone.trim()) {
      // Telefone é opcional — só pula.
      onNext();
      return;
    }
    saveMut.mutate();
  };

  return (
    <StepLayout
      illustration={<ContactArt />}
      title="Como seus pacientes te encontram?"
      subtitle="Informe o WhatsApp principal da clínica. Usamos pra enviar lembretes e confirmações automáticas."
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-ink-700">
            WhatsApp principal (opcional)
          </span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(11) 99999-9999"
            className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            data-testid="wizard-phone"
            inputMode="tel"
          />
          <span className="mt-1 block text-xs text-ink-500">
            Pode incluir DDD. Você pode conectar a API oficial do WhatsApp
            depois em Integrações.
          </span>
        </label>
        <StepActions
          onBack={onBack}
          onSkip={onSkip}
          onNext={handleNext}
          nextLabel={saveMut.isPending ? 'Salvando…' : 'Próximo'}
          nextDisabled={saveMut.isPending}
        />
      </div>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 3 — Horário de atendimento simplificado.
// ───────────────────────────────────────────────────────────────────
function ScheduleStep({
  onNext,
  onBack,
  onSkip,
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const { data: tenant } = useQuery<TenantMe>({
    queryKey: ['tenant-me'],
    queryFn: async () => (await api.get('/tenants/me')).data,
  });

  const [startHour, setStartHour] = useState(8);
  const [endHour, setEndHour] = useState(18);
  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]);

  useEffect(() => {
    if (tenant) {
      setStartHour(tenant.scheduleStartHour ?? 8);
      setEndHour(tenant.scheduleEndHour ?? 18);
    }
  }, [tenant]);

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch('/tenants/me/schedule', {
        scheduleStartHour: startHour,
        scheduleEndHour: endHour,
        scheduleWorkingDays: workingDays,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-me'] });
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Falha ao salvar horário.');
    },
  });

  const toggleDay = (d: number) => {
    setWorkingDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort(),
    );
  };

  const DAYS = [
    { num: 1, label: 'Seg' },
    { num: 2, label: 'Ter' },
    { num: 3, label: 'Qua' },
    { num: 4, label: 'Qui' },
    { num: 5, label: 'Sex' },
    { num: 6, label: 'Sáb' },
    { num: 0, label: 'Dom' },
  ];

  return (
    <StepLayout
      illustration={<ScheduleArt />}
      title="Qual o horário de atendimento?"
      subtitle="A agenda mostra só o intervalo que você atender. Pode ajustar por dia depois em Configurações."
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-ink-700">Abre às</span>
            <select
              value={startHour}
              onChange={(e) => setStartHour(Number(e.target.value))}
              className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none bg-white"
              data-testid="wizard-start-hour"
            >
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, '0')}:00
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-700">Fecha às</span>
            <select
              value={endHour}
              onChange={(e) => setEndHour(Number(e.target.value))}
              className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none bg-white"
              data-testid="wizard-end-hour"
            >
              {Array.from({ length: 25 }, (_, i) => i).map((h) => (
                <option key={h} value={h}>
                  {h === 24 ? '24:00' : `${String(h).padStart(2, '0')}:00`}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div>
          <span className="text-sm font-medium text-ink-700 block mb-2">
            Dias de funcionamento
          </span>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => {
              const active = workingDays.includes(d.num);
              return (
                <button
                  key={d.num}
                  type="button"
                  onClick={() => toggleDay(d.num)}
                  className={
                    active
                      ? 'px-3.5 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700'
                      : 'px-3.5 py-2 rounded-lg border border-ink-200 text-ink-700 text-sm font-medium hover:border-brand-300'
                  }
                  data-testid={`wizard-day-${d.num}`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </div>
        <StepActions
          onBack={onBack}
          onSkip={onSkip}
          onNext={() => saveMut.mutate()}
          nextLabel={saveMut.isPending ? 'Salvando…' : 'Próximo'}
          nextDisabled={
            saveMut.isPending || workingDays.length === 0 || endHour <= startHour
          }
        />
      </div>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 4 — Primeiro serviço/procedimento (cria de verdade).
// ───────────────────────────────────────────────────────────────────
const SERVICE_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'dentistry', label: 'Odontologia' },
  { value: 'aesthetic', label: 'Estética' },
  { value: 'hygiene', label: 'Higiene / Limpeza' },
  { value: 'orthodontics', label: 'Ortodontia' },
  { value: 'surgery', label: 'Cirurgia' },
  { value: 'physiotherapy', label: 'Fisioterapia' },
  { value: 'psychology', label: 'Psicologia' },
  { value: 'nutrition', label: 'Nutrição' },
  { value: 'dermatology', label: 'Dermatologia' },
  { value: 'podiatry', label: 'Podologia' },
  { value: 'other', label: 'Outro' },
];

/** "150,00" | "150" | "R$ 150,50" → centavos (int). Vazio/invalid → 0. */
function parseBrlToCents(raw: string): number {
  const cleaned = raw.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '');
  const normalized = cleaned.replace(',', '.');
  const value = Number.parseFloat(normalized);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * 100);
}

/** Deriva um `code` válido (A-Z0-9-_) a partir do nome + sufixo curto. */
function deriveServiceCode(name: string): string {
  const base = name
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase();
  return `${base || 'SERV'}-${suffix}`;
}

function ServiceStep({
  onNext,
  onBack,
  onSkip,
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [duration, setDuration] = useState(30);
  const [category, setCategory] = useState('dentistry');

  const saveMut = useMutation({
    mutationFn: () =>
      api.post('/services', {
        code: deriveServiceCode(name),
        name: name.trim(),
        category,
        durationMinutes: duration,
        basePriceCents: parseBrlToCents(price),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services'] });
      qc.invalidateQueries({ queryKey: ['tenant-onboarding'] });
      toast.success('Serviço criado!');
      onNext();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Falha ao criar serviço.');
    },
  });

  const handleNext = () => {
    if (!name.trim() || name.trim().length < 2) {
      toast.error('Dê um nome ao serviço (ex.: Consulta, Limpeza).');
      return;
    }
    saveMut.mutate();
  };

  return (
    <StepLayout
      illustration={<ServiceArt />}
      title="Cadastre seu primeiro serviço"
      subtitle="É o que você oferece e cobra — consulta, limpeza, botox… Serve pra agendar e montar planos de tratamento. Você adiciona mais depois em Serviços."
    >
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-medium text-ink-700">Nome do serviço</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Consulta de avaliação"
            className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            data-testid="wizard-service-name"
            autoFocus
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-sm font-medium text-ink-700">Preço (R$)</span>
            <input
              type="text"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="150,00"
              inputMode="decimal"
              className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
              data-testid="wizard-service-price"
            />
            <span className="mt-1 block text-xs text-ink-500">
              Pode deixar 0 (cortesia/reavaliação).
            </span>
          </label>
          <label className="block">
            <span className="text-sm font-medium text-ink-700">Duração</span>
            <select
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none bg-white"
              data-testid="wizard-service-duration"
            >
              {[15, 30, 45, 60, 90, 120].map((m) => (
                <option key={m} value={m}>
                  {m} min
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-ink-700">Categoria</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 block w-full rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none bg-white"
            data-testid="wizard-service-category"
          >
            {SERVICE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <StepActions
          onBack={onBack}
          onSkip={onSkip}
          onNext={handleNext}
          nextLabel={saveMut.isPending ? 'Salvando…' : 'Próximo'}
          nextDisabled={saveMut.isPending}
        />
      </div>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 5 — Logo (placeholder).
// ───────────────────────────────────────────────────────────────────
const LOGO_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const LOGO_MAX_BYTES = 2 * 1024 * 1024; // 2 MB (espelha o backend)

function LogoStep({
  onNext,
  onBack,
  onSkip,
}: {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState('');

  // Mesmo endpoint da seção de Branding: sobe pro storage e já persiste
  // logoUrl/logoKey no tema. Retorna a URL pública estável.
  const uploadMut = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/tenants/me/branding/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as { logoUrl: string };
    },
    onSuccess: (res) => {
      setLogoUrl(res.logoUrl);
      toast.success('Logo enviado!');
      qc.invalidateQueries({ queryKey: ['tenant-branding'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message ?? 'Falha ao enviar o logo.'),
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-selecionar o mesmo arquivo
    if (!file) return;
    if (!LOGO_ACCEPT.split(',').includes(file.type)) {
      toast.error('Formato não suportado. Use PNG, JPG, WEBP ou SVG.');
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      toast.error('Arquivo maior que 2 MB.');
      return;
    }
    uploadMut.mutate(file);
  }

  return (
    <StepLayout
      illustration={<LogoArt />}
      title="Quer subir o logo da sua clínica?"
      subtitle="Ele aparece nas notas fiscais, e-mails, portal do paciente e sala de espera. Pode pular e fazer depois em Configurações → Branding."
    >
      <div className="space-y-4">
        <input
          ref={fileRef}
          type="file"
          accept={LOGO_ACCEPT}
          onChange={onPickFile}
          disabled={uploadMut.isPending}
          data-testid="wizard-logo-file"
          className="hidden"
        />

        {logoUrl ? (
          <div className="flex items-center gap-4 rounded-lg border border-ink-200 bg-white p-4">
            <div className="w-16 h-16 rounded-lg border border-ink-200 bg-ink-50 overflow-hidden flex items-center justify-center flex-shrink-0">
              <img src={logoUrl} alt="Logo enviado" className="w-full h-full object-contain" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-700">Logo salvo ✓</p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploadMut.isPending}
                className="mt-1 text-xs text-ink-500 hover:text-brand-700 disabled:opacity-50"
              >
                Trocar imagem
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadMut.isPending}
            data-testid="wizard-logo-upload"
            className="w-full rounded-lg border border-dashed border-ink-300 bg-ink-50/40 p-6 text-center hover:border-brand-300 hover:bg-brand-50/30 transition-colors disabled:opacity-60"
          >
            <svg className="w-12 h-12 mx-auto text-ink-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="text-sm text-ink-600">
              {uploadMut.isPending ? 'Enviando…' : 'Clique para enviar o logo'}
            </p>
            <p className="text-[10px] text-ink-400 mt-1">PNG, JPG, WEBP ou SVG · até 2 MB.</p>
          </button>
        )}

        <StepActions
          onBack={onBack}
          onSkip={onSkip}
          onNext={onNext}
          nextLabel="Próximo"
        />
      </div>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step 5 — Primeiro paciente (link, não criação inline).
// ───────────────────────────────────────────────────────────────────
function PatientStep({
  onFinish,
  onBack,
  onGoPatients,
}: {
  onFinish: () => void;
  onBack: () => void;
  onGoPatients: () => void;
}) {
  // Criar paciente direto exige CPF + endereço (validação backend
  // obrigatória pra emissão de NFS-e). Por isso aqui só convidamos o
  // owner pra ir ao /pacientes — onde tem o form completo.
  return (
    <StepLayout
      illustration={<PatientArt />}
      title="Quase lá! Vamos cadastrar o primeiro paciente?"
      subtitle="Você pode criar manualmente ou importar uma planilha com várias linhas de uma vez. Skip se quiser fazer isso depois."
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onGoPatients}
            className="text-left p-4 rounded-lg border border-ink-200 hover:border-brand-400 hover:shadow-sm bg-white transition-all"
            data-testid="wizard-goto-patients"
          >
            <span className="block text-2xl mb-2">👤</span>
            <span className="block text-sm font-semibold text-ink-900">
              Cadastrar manual
            </span>
            <span className="block text-xs text-ink-500 mt-1">
              Vou pra tela de pacientes e crio um agora.
            </span>
          </button>
          <button
            type="button"
            onClick={onGoPatients}
            className="text-left p-4 rounded-lg border border-ink-200 hover:border-brand-400 hover:shadow-sm bg-white transition-all"
            data-testid="wizard-import-patients"
          >
            <span className="block text-2xl mb-2">📥</span>
            <span className="block text-sm font-semibold text-ink-900">
              Importar planilha
            </span>
            <span className="block text-xs text-ink-500 mt-1">
              XLSX ou CSV — valido antes de gravar nada.
            </span>
          </button>
        </div>
        <StepActions
          onBack={onBack}
          onSkip={onFinish}
          onNext={onFinish}
          nextLabel="Finalizar"
          skipLabel="Fazer depois"
        />
      </div>
    </StepLayout>
  );
}

// ───────────────────────────────────────────────────────────────────
// Step layout comum + actions.
// ───────────────────────────────────────────────────────────────────
function StepLayout({
  illustration,
  title,
  subtitle,
  children,
}: {
  illustration: React.ReactNode;
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-start">
      <div className="order-2 lg:order-1">
        <div className="lg:sticky lg:top-32">{illustration}</div>
      </div>
      <div className="order-1 lg:order-2">
        <h2
          id="wizard-title"
          className="text-2xl sm:text-3xl font-bold text-ink-900"
        >
          {title}
        </h2>
        <p className="mt-2 text-ink-600">{subtitle}</p>
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

function StepActions({
  onBack,
  onSkip,
  onNext,
  nextLabel,
  skipLabel = 'Pular',
  showBack = true,
  nextDisabled = false,
}: {
  onBack?: () => void;
  onSkip: () => void;
  onNext: () => void;
  nextLabel: string;
  skipLabel?: string;
  showBack?: boolean;
  nextDisabled?: boolean;
}) {
  return (
    <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
      <div className="flex gap-2">
        {showBack && onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-ink-600 hover:bg-ink-50"
            data-testid="wizard-back"
          >
            ← Voltar
          </button>
        )}
        <button
          type="button"
          onClick={onSkip}
          className="px-4 py-2.5 rounded-lg text-sm font-medium text-ink-500 hover:bg-ink-50"
          data-testid="wizard-skip"
        >
          {skipLabel}
        </button>
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className="px-5 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
        data-testid="wizard-next"
      >
        {nextLabel}
      </button>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Ilustrações SVG por step — pequenas, decorativas, sem deps.
// ───────────────────────────────────────────────────────────────────

function ArtFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative rounded-2xl bg-gradient-to-br from-brand-50 via-white to-accent-50/50 p-6 sm:p-8 border border-brand-100">
      {children}
    </div>
  );
}

function WelcomeArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <circle cx="180" cy="100" r="60" fill="#ccfbf1" />
        <circle cx="180" cy="100" r="40" fill="#0d9488" />
        <path
          d="M168 90c-3 0-5 2-5 5 0 4 4 12 6 18 1 2 2 3 4 3 1 0 2-1 2-3 0-2 1-3 3-3s3 1 3 3 1 3 2 3c2 0 3-1 4-3 2-6 6-14 6-18 0-3-2-5-5-5-2 0-3 1-4 1s-3-1-4-1c-2 0-3 1-4 1s-3-1-5-1z"
          fill="#ffffff"
        />
        <rect x="60" y="190" width="240" height="56" rx="10" fill="#ffffff" stroke="#99f6e4" />
        <rect x="80" y="206" width="120" height="10" rx="5" fill="#0f172a" />
        <rect x="80" y="222" width="180" height="8" rx="4" fill="#94a3b8" />
        <circle cx="280" cy="218" r="14" fill="#22d3ee" />
        <path d="M276 218l3 3 6-6" stroke="#ffffff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </ArtFrame>
  );
}

function ContactArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <rect x="120" y="40" width="120" height="200" rx="20" fill="#ffffff" stroke="#5eead4" strokeWidth="2" />
        <rect x="132" y="60" width="96" height="160" rx="6" fill="#f0fdfa" />
        <circle cx="180" cy="234" r="6" fill="#0d9488" />
        <rect x="148" y="80" width="64" height="10" rx="5" fill="#0d9488" />
        <rect x="148" y="100" width="48" height="6" rx="3" fill="#94a3b8" />
        <rect x="148" y="120" width="64" height="40" rx="6" fill="#ccfbf1" />
        <rect x="155" y="130" width="50" height="6" rx="3" fill="#0f766e" />
        <rect x="155" y="142" width="35" height="6" rx="3" fill="#0f766e" />
        <rect x="148" y="170" width="64" height="34" rx="6" fill="#cffafe" />
        <rect x="155" y="180" width="40" height="6" rx="3" fill="#0e7490" />
        <circle cx="290" cy="80" r="20" fill="#25D366" />
        <path
          d="M285 76l4 4-2 5c2 3 5 6 8 8l5-2 4 4c-1 4-5 5-9 4-7-1-13-7-14-14 0-3 1-9 4-9z"
          fill="#ffffff"
        />
      </svg>
    </ArtFrame>
  );
}

function ScheduleArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <rect x="60" y="40" width="240" height="200" rx="12" fill="#ffffff" stroke="#5eead4" strokeWidth="2" />
        <rect x="60" y="40" width="240" height="36" rx="12" fill="#0d9488" />
        <rect x="78" y="54" width="100" height="10" rx="5" fill="#ffffff" />
        <circle cx="270" cy="58" r="6" fill="#5eead4" />
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <rect x="76" y={92 + i * 28} width="30" height="10" rx="5" fill="#cbd5e1" />
            <rect
              x={120}
              y={88 + i * 28}
              width={[140, 100, 160, 80, 130][i]}
              height="20"
              rx="5"
              fill={i === 1 ? '#22d3ee' : '#5eead4'}
              opacity="0.85"
            />
          </g>
        ))}
      </svg>
    </ArtFrame>
  );
}

function ServiceArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <rect x="60" y="50" width="240" height="180" rx="12" fill="#ffffff" stroke="#5eead4" strokeWidth="2" />
        {[0, 1, 2].map((i) => (
          <g key={i}>
            <rect x="80" y={72 + i * 50} width="200" height="38" rx="8" fill={i === 0 ? '#f0fdfa' : '#ffffff'} stroke="#99f6e4" />
            <circle cx="102" cy={91 + i * 50} r="10" fill={['#0d9488', '#22d3ee', '#5eead4'][i]} />
            <rect x="122" y={82 + i * 50} width="90" height="8" rx="4" fill="#0f172a" />
            <rect x="122" y={95 + i * 50} width="50" height="7" rx="3.5" fill="#94a3b8" />
            <rect x="232" y={86 + i * 50} width="36" height="12" rx="6" fill="#ccfbf1" />
          </g>
        ))}
      </svg>
    </ArtFrame>
  );
}

function LogoArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <rect x="60" y="60" width="240" height="160" rx="16" fill="#ffffff" stroke="#5eead4" strokeWidth="2" strokeDasharray="6 4" />
        <circle cx="180" cy="125" r="38" fill="#ccfbf1" />
        <path d="M165 125l10 10 22-22" stroke="#0d9488" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <rect x="120" y="180" width="120" height="10" rx="5" fill="#0f172a" />
        <rect x="140" y="198" width="80" height="6" rx="3" fill="#94a3b8" />
      </svg>
    </ArtFrame>
  );
}

function PatientArt() {
  return (
    <ArtFrame>
      <svg viewBox="0 0 360 280" className="w-full h-auto" aria-hidden>
        <rect x="50" y="60" width="260" height="160" rx="12" fill="#ffffff" stroke="#5eead4" strokeWidth="2" />
        <circle cx="120" cy="120" r="28" fill="#cffafe" />
        <circle cx="120" cy="112" r="10" fill="#0e7490" />
        <path
          d="M100 140c0-12 9-20 20-20s20 8 20 20"
          fill="#0e7490"
        />
        <rect x="170" y="100" width="120" height="12" rx="6" fill="#0f172a" />
        <rect x="170" y="120" width="80" height="8" rx="4" fill="#94a3b8" />
        <rect x="170" y="138" width="100" height="8" rx="4" fill="#94a3b8" />
        <rect x="170" y="160" width="60" height="22" rx="6" fill="#0d9488" />
        <rect x="180" y="167" width="40" height="8" rx="4" fill="#ffffff" />
        <circle cx="290" cy="76" r="18" fill="#22d3ee" />
        <path d="M285 76l4 4 6-7" stroke="#ffffff" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </ArtFrame>
  );
}
